import { Checkbox, FormControl, FormControlLabel } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { useOutletContext } from "react-router-dom";

import InputGeneric from "../../components/InputGeneric";
import { Acquirer, AcquirerMetadata } from "./types";

type AsaasAccountGroup = {
    title: string;
    activeKey: string;
    apiKey: string;
    keys: string[];
    labels: Record<string, string>;
};

const ASAAS_ACCOUNT_GROUPS: AsaasAccountGroup[] = [
    {
        title: "Conta padrão",
        activeKey: "asaas_account_active",
        apiKey: "asaas_api_key",
        keys: ["asaas_account_name", "asaas_api_key", "asaas_app_name"],
        labels: {
            asaas_account_name: "Nome da conta",
            asaas_api_key: "API Key",
            asaas_app_name: "App Name",
        },
    },
    {
        title: "Conta secundária",
        activeKey: "asaas_account_active_secondary",
        apiKey: "asaas_api_key_secondary",
        keys: [
            "asaas_account_name_secondary",
            "asaas_api_key_secondary",
            "asaas_app_name_secondary",
        ],
        labels: {
            asaas_account_name_secondary: "Nome da conta",
            asaas_api_key_secondary: "API Key",
            asaas_app_name_secondary: "App Name",
        },
    },
    {
        title: "Conta terciária",
        activeKey: "asaas_account_active_tertiary",
        apiKey: "asaas_api_key_tertiary",
        keys: [
            "asaas_account_name_tertiary",
            "asaas_api_key_tertiary",
            "asaas_app_name_tertiary",
        ],
        labels: {
            asaas_account_name_tertiary: "Nome da conta",
            asaas_api_key_tertiary: "API Key",
            asaas_app_name_tertiary: "App Name",
        },
    },
];

const ASAAS_META_KEYS = new Set(
    ASAAS_ACCOUNT_GROUPS.flatMap((group) => [...group.keys, group.activeKey])
);

const isMetaActive = (value?: string | null): boolean =>
    ["1", "true", "yes"].includes(
        String(value ?? "")
            .trim()
            .toLowerCase()
    );

export function getAsaasActiveAccountValidationError(
    acquirer: Acquirer | null
): string | null {
    if (!acquirer) {
        return "Selecione uma adquirente.";
    }

    const isAsaasAcquirer = acquirer.acquirer_meta_data.some(
        (meta) => meta.key === "asaas_api_key"
    );
    if (!isAsaasAcquirer) {
        return null;
    }

    const values = Object.fromEntries(
        acquirer.acquirer_meta_data.map((meta) => [meta.key, meta.value])
    );

    const errors = ASAAS_ACCOUNT_GROUPS.flatMap((group) => {
        const isActive =
            values[group.activeKey] !== undefined
                ? isMetaActive(values[group.activeKey])
                : String(values[group.apiKey] ?? "").trim() !== "";

        if (!isActive) {
            return [];
        }

        const missing = group.keys.filter(
            (key) => String(values[key] ?? "").trim() === ""
        );

        if (missing.length === 0) {
            return [];
        }

        return [
            `A ${group.title.toLowerCase()} está ativa e precisa ter ${missing
                .map((key) => group.labels[key] ?? key)
                .join(", ")} preenchido.`,
        ];
    });

    return errors[0] ?? null;
}

type OutletContextType = {
    activeAcquirer: Acquirer | null;
    setActiveAcquirer: Dispatch<SetStateAction<Acquirer | null>>;
};

export default function AcquirerSettingsList() {
    const { activeAcquirer, setActiveAcquirer } =
        useOutletContext<OutletContextType>();

    const updateMetaValue = (key: string, value: string) => {
        if (!activeAcquirer) {
            return;
        }

        setActiveAcquirer({
            ...activeAcquirer,
            acquirer_meta_data: activeAcquirer.acquirer_meta_data.map((meta) =>
                meta.key === key ? { ...meta, value } : meta
            ),
        });
    };

    const findMeta = (key: string): AcquirerMetadata | undefined =>
        activeAcquirer?.acquirer_meta_data.find((meta) => meta.key === key);

    const isAsaasAcquirer = activeAcquirer?.acquirer_meta_data.some(
        (meta) => meta.key === "asaas_api_key"
    );

    const otherMetadata =
        activeAcquirer?.acquirer_meta_data.filter(
            (meta) => !ASAAS_META_KEYS.has(meta.key)
        ) ?? [];

    return (
        <FormControl fullWidth style={{ marginTop: 24, marginBottom: 24 }}>
            {isAsaasAcquirer ? (
                <div className="asaasAccounts">
                    {ASAAS_ACCOUNT_GROUPS.map((group) => {
                        const activeMeta = findMeta(group.activeKey);

                        return (
                            <div
                                className="asaasAccountGroup"
                                key={group.title}
                            >
                                <div className="asaasAccountGroupHeader">
                                    <h3>{group.title}</h3>
                                    {activeMeta ? (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={isMetaActive(
                                                        activeMeta.value
                                                    )}
                                                    onChange={(event) => {
                                                        updateMetaValue(
                                                            group.activeKey,
                                                            event.target.checked
                                                                ? "1"
                                                                : "0"
                                                        );
                                                    }}
                                                />
                                            }
                                            label="Status"
                                        />
                                    ) : null}
                                </div>
                                {group.keys.map((key) => {
                                    const meta = findMeta(key);
                                    if (!meta) {
                                        return null;
                                    }

                                    return (
                                        <InputGeneric
                                            label={group.labels[key] ?? key}
                                            fullWidth
                                            key={key}
                                            value={meta.value ?? ""}
                                            onChange={(e) => {
                                                updateMetaValue(
                                                    key,
                                                    e.target.value
                                                );
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            ) : null}

            {otherMetadata.map((meta) => (
                <InputGeneric
                    label={meta.key}
                    fullWidth
                    key={meta.key}
                    value={meta.value ?? ""}
                    onChange={(e) => {
                        updateMetaValue(meta.key, e.target.value);
                    }}
                />
            ))}
        </FormControl>
    );
}
